import React, {useEffect, useRef} from "react";
import {useGlobalStyles} from "../../../utils/styles";
import {getAttributes, SUPPLIER_SYNDICATION_ENABLED, useDidUpdateEffect, utilGetPartners, utilSearchLastImport, utilSearchLastImports} from "../../../utils/utils";
import axios from "axios";
import Config from "../../../../config";
import {loadProfiles} from "../../../../files/import_export_profiles";
import {Helpers, StorageHelper} from "../../../../helpers";
import cloneDeep from 'lodash/cloneDeep';
import {Grid, IconButton, Paper, Typography} from "@material-ui/core";
import ProductImport from "./components/ProductImport";
import SelectCategory from "./components/SelectCategory";
import CategoryMapping from "./components/CategoryMapping";
import AttributeMapping from "./components/AttributeMapping";
import ListMapping from "./components/ListMapping";
import Review, {formatProfileAttributeRulesToRules, formatProfileCategoryMappingsToRules, formatProfileListMappingsToRules, formatProfileToColumns} from "./components/Review";
import EnhancedContentLoader from "../../../components/EnhancedContentLoader";
import {ArrowBack} from "@material-ui/icons";
import EnhancedStepper from "../../../components/EnhancedStepper";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import {makeStyles} from "@material-ui/core/styles";
import isEmpty from "lodash/isEmpty";
import {SaveProfile} from "../../export/components/EnhancedProfile";
import UpdatingDialog from "../../../components/Dialogs/UpdatingDialog";

export const useAdvancedSupplierImportStyles = makeStyles(theme => ({
    mappingHeader: {
        color: "black",
        fontWeight: 700
    },
    mappingCell: {
        margin: "12px",
        border: "none"
    }
}));

const storageHelper = new StorageHelper();

const blankProfile = {
    DocumentType: "ProductOnboardingCenterCatalog",
    Name: undefined,
    Partner: "",
    Category: "",
    CategoryMappings: {},
    AttributeMappings: {},
    ListMappings: {},
    AttributeMappingRules: []
};

const unimportableAttributesAsSupplier = [
    "approval_date", "approval_status", "approved_by", "private_note", "public_note", "sync_status", "listed_by", "listed_date", "delisted_by", "delisted_date", "created_date"
];

const unimportableAttributesAsRetailer = [
    "private_note", "public_note", "created_date"
];

export const unescapeField = (field) => {
    if (typeof field === "string" && field.startsWith("{") && field.endsWith("}")) {
        return field.slice(1, -1);
    }
    return field;
};

export function unescapeFieldsInCondition(condition) {
    if (!condition) return condition;
    if (condition.Conditions && Array.isArray(condition.Conditions)) {
        return {
            ...condition,
            Conditions: condition.Conditions.map(unescapeFieldsInCondition)
        };
    } else {
        return {
            ...condition,
            Field: unescapeField(condition.Field)
        };
    }
}

function getStepContent(step, props) {
    switch (step) {
        case 0:
            return <ProductImport {...props}/>;
        case 1:
            return <SelectCategory {...props}/>;
        case 2:
            return <CategoryMapping {...props}/>;
        case 3:
            return <AttributeMapping {...props} fileParsedFields={props.parsedFields} />;
        case 4:
            return <ListMapping {...props}/>;
        case 5:
            return <Review {...props}/>;
        default:
            return <></>
    }
}

export const formatProfileFromRemote = (remoteProfile) => {
    let filterStringStored = {};

    //region Backwards compatibility with old profiles
    let parsedFilter = remoteProfile.Filter;
    if (
        parsedFilter !== undefined && parsedFilter !== ""
        && (remoteProfile.Fields === undefined || remoteProfile.Fields.length === 0)
        && (remoteProfile.Rules === undefined || remoteProfile.Rules.length === 0)
    ) {
        const parsed = JSON.parse(parsedFilter);

        filterStringStored = {
            Name: remoteProfile.Name,
            Description: remoteProfile.Description,
            DocumentType: remoteProfile.DocumentType,
            Partner: parsed.Partner,
            Category: parsed.Category,
            CategoryMappings: parsed.CategoryMappings,
            AttributeMappings: parsed.AttributeMappings,
            ListMappings: parsed.ListMappings
        }
    }
    //endregion

    let partner = remoteProfile.Partner;
    let categoryColFromFile = "";
    if (remoteProfile.Fields) {
        categoryColFromFile = remoteProfile.Fields.find(col => col.TargetField === "category");
        if(!categoryColFromFile) categoryColFromFile = "";
        if(categoryColFromFile.SourceField === null) categoryColFromFile = "";

        // Remove braces from escaping process
        if (!isEmpty(categoryColFromFile))
            categoryColFromFile = categoryColFromFile.SourceField.replace(/{|}/g, "");
    }

    let attributeMappings = [];
    if (remoteProfile.Fields) {
        attributeMappings = remoteProfile.Fields.reduce((acc, col) => {
            let targetField = col.TargetField;
            let sourceField = col.SourceField;
            if(!targetField || !sourceField) return acc;

            // Remove braces from escaping process
            sourceField = sourceField.replace("{", "").replace("}", "");

            return {
                ...acc,
                [targetField]: sourceField
            }
        }, {});
    }

    let categoryMappings = [];
    if (remoteProfile.Rules) {
        categoryMappings = remoteProfile.Rules.filter(rule => rule.TargetField === categoryColFromFile).reduce((acc, rule) => {
            let source = rule.SourceField;
            let condition = rule.Condition.Value;

            // Remove " from escaping process
            condition = condition.replace(/"/g, "");
            source = source.replace(/"/g, "");

            return {
                ...acc,
                [condition]: source
            }
        }, []);
    }

    let listMappings = [];
    if (remoteProfile.Rules) {
        listMappings = remoteProfile.Rules.filter(rule => (rule.TargetField !== categoryColFromFile) && rule.Type !== "AdvancedAttributeRule").reduce((acc, rule) => {
            if(!rule.TargetField || !rule.SourceField) return acc;
            let source = rule.SourceField;
            let condition = rule.Condition.Value;
            let mappedField = Object.entries(attributeMappings)
                .find(([key, value]) => value === rule.TargetField);

            if (mappedField === undefined) mappedField = ["", ""];

            // Remove " from escaping process
            source = source.replace(/"/g, "");
            condition = condition.replace(/"/g, "");

            return {
                ...acc,
                [mappedField[0]]: {
                    ...acc[mappedField[0]],
                    [source]: condition
                }
            }
        }, []);
    }

    let advancedAttributeRules = [];
    if (remoteProfile.Rules) {
        advancedAttributeRules = remoteProfile.Rules.filter(rule => rule.Type === "AdvancedAttributeRule");
        advancedAttributeRules = advancedAttributeRules.map(rule => ({
            ...rule,
            Condition: unescapeFieldsInCondition(rule.Condition)
        }));
    }

    return {
        ...remoteProfile,
        Id: remoteProfile.Id,
        Shared: remoteProfile.Shared,
        Name: remoteProfile.Name || filterStringStored.Name,
        Description: remoteProfile.Description || filterStringStored.Description,
        DocumentType: remoteProfile.DocumentType || filterStringStored.DocumentType,
        Partner: partner || filterStringStored.Partner,
        Category: categoryColFromFile || filterStringStored.Category,
        CategoryMappings: categoryMappings || filterStringStored.CategoryMappings,
        AttributeMappings: attributeMappings || filterStringStored.AttributeMappings,
        ListMappings: listMappings || filterStringStored.ListMappings,
        AttributeMappingRules: advancedAttributeRules || []
    }
}

export default function CatalogManagerProductAdvancedImport(props) {
    const globalClasses = useGlobalStyles();

    const abortController = useRef(null);

    const isMarketplace = props.retailers && props.retailers.some(retailer => retailer.Marketplace === true);

    const urlStartingStep = new Helpers().queryString(props.location.search)["startingStep"];
    const urlPartnerId = new Helpers().queryString(props.location.search)["partnerId"];
    const urlProfileName = new Helpers().queryString(props.location.search)["profile"];

    const storageProfileName = "LBCatalogSupplierImportProfile";
    let profileType = "ImportCatalogItems";

    const [steps, setSteps] = React.useState(
        [
            {label: props.isRetailer
                    ? "Product Import"
                    : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                        ? "Select Source"
                        : "Product Import",
                status: ""
            },
            {label: "Select Category", status: ""},
            {label: "Category Mapping", status: ""},
            {label: "Attribute Mapping", status: ""},
            {label: "List Mapping", status: ""},
            {label: "Review & Submit", status: ""}
        ]
    );
    const isInitialized = useRef(false);
    const [resfreshView, setRefreshView] = React.useState(false);

    const [updated, setUpdated] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const [error, setError] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(0);

    const [attributes, setAttributes] = React.useState([]);
    const [partners, setPartners] = React.useState([]);

    /* We need to keep track of two profiles, one is the formatted variant to fit into
       set objects in the backend, and the other is the raw variant to fit into the UI.
       Normally you could format on save but the profile components reuse existing save logic. */
    const [profile, setProfile] = React.useState({...blankProfile});
    const [formattedProfile, setFormattedProfile] = React.useState({});
    const [availableProfiles, setAvailableProfiles] = React.useState([]);

    const previousPartner = useRef(profile.Partner);

    //region Import State
    const [hasPreviousImport, setHasPreviousImport] = React.useState(false);
    const [previousImports, setPreviousImports] = React.useState([]);
    const [loadingPreviousImport, setLoadingPreviousImport] = React.useState(false); // Single file loading for PARTNER
    const [loadingPreviousFiles, setLoadingPreviousFiles] = React.useState(false); // General file loading for ALL PARTNERS

    const [file, setFile] = React.useState(null);
    const [parsedFields, setParsedFields] = React.useState([]);
    const [parsedFieldsWithData, setParsedFieldsWithData] = React.useState([]);
    const [sampleData, setSampleData] = React.useState([]);
    //endregion

    //region Functions
    const allowNext = () => {
        switch (activeStep) {
            case 0:
                return file !== null
                    && (profile.Partner !== "" && profile.Partner !== "000000")
                    && (profile.Name !== "" || profile.Name !== undefined);
            case 1:
                return profile.Category !== "";
            case 2:
                return true;
            case 3:
                return true;
            case 4:
                return true;
            case 5:
                return false;
            default:
                return false;
        }
    }
    const allowBack = () => {
        switch (activeStep) {
            case 0:
                return false;
            case 1:
                return true;
            case 2:
                return true;
            case 3:
                return true;
            case 4:
                return true;
            case 5:
                return true;
            default:
                return false;
        }
    }
    const nextStep = () => {
        if (!allowNext()) return;
        setActiveStep(activeStep + 1);
        setSteps((prevState) => {
            let newState = cloneDeep(prevState);
            // newState[activeStep].status = "success";
            newState[activeStep + 1].status = "active";
            return newState;
        });
    }
    const backStep = () => {
        if (!allowBack()) return;
        setActiveStep(activeStep - 1);
    }

    const onUploadDrop = async (files) => {
        if (!files || !files.length) return;
        setFile(files[0]);
        setLoadingPreviousImport(true);
        await parseFile(files[0]);
        setLoadingPreviousImport(false);
    }
    const parseFile = async (file) => {
        let fileType = 'csv';
        if (file.name && file.name.endsWith(".xlsx")) fileType = 'xlsx';

        try {
            let formData = new FormData();
            formData.append("file", file);

            let parsed = (await axios.post(Config.api + `/odata/company/Functions.ParseFlatFile?docType=ProductOnboardingCenterCatalog&fileType=${fileType}`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            })).data;

            let uniqueColumns = parsed.Columns.map(r => r.Input);
            let parsedWithData = (await axios.post(Config.api + `/odata/company/Functions.ParseFlatFile?docType=ProductOnboardingCenterCatalog&fileType=${fileType}&uniqueColumns=${uniqueColumns}&duplicateColumns=${uniqueColumns}`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            })).data;

            let mappedFields = parsed.Columns.map(r => ({field: r.Input, content: r.SampleContent}));
            setParsedFields(mappedFields);
            setParsedFieldsWithData(parsedWithData.Columns.map(r => ({
                field: r.Input,
                content: r.SampleContent,
                values: r.UniqueValues,
                duplicates: r.DuplicateValues
            })));

            let sampleData = parsed.SampleRows.filter(c => {
                let values = Object.values(c);
                const result = values.every(v => v === "");
                return  !result;
            });
            setSampleData(sampleData);
        } catch (e) {
            let errors = ["Unexpected error occurred."];
            if (e.response) {
                errors = new Helpers().getApiErrors(e.response.data);
            }
            let errorMsg = errors.join(" ");
            setError({
                open: true,
                errorHeader: "Error parsing file",
                errorMessage: errorMsg
            })
        }
    }

    const loadImportProfiles = async () => {
        let profiles = await loadProfiles(profileType);
        profiles = profiles.map(r => formatProfileFromRemote(r));
        setAvailableProfiles(profiles);
        return profiles;
    }
    const loadPartners = async () => {
        let partners = await utilGetPartners();
        if (partners.errorMessage) {
            setError({open: true, errorHeader: "Error", errorMessage: partners.errorMessage});
            return [];
        }

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        return sortedPartners;
    }
    const loadAttributes = async (partnerId) => {
        let attributes = await getAttributes(partnerId, props.isRetailer);
        if (attributes.errorMessage) {
            setError({open: true, errorHeader: "Error", errorMessage: attributes.errorMessage});
            return;
        }

        // Filter out any unimportable attributes
        let unimportableAttributes = props.isRetailer ? unimportableAttributesAsRetailer : unimportableAttributesAsSupplier;
        attributes = attributes.filter(r => !unimportableAttributes.includes(r.Name));

        setAttributes(attributes);
    }
    const loadPreviousImport = async () => {
        // Cancel any existing requests
        if (abortController.current) {
            abortController.current.abort();
        }

        // Create new abort controller
        abortController.current = new AbortController();

        setLoadingPreviousImport(true);

        // Set up timeout to abort after 15 seconds
        const timeoutId = setTimeout(() => {
            if (abortController.current) {
                abortController.current.abort();
            }
        }, 15000);

        try {
            let response = await utilSearchLastImport(profile.Partner, abortController.current.signal);
            clearTimeout(timeoutId);
            if(!response.success) {
                setActiveStep(0);
                setError({open: true, errorHeader: "Error", errorMessage: response.errorMessage ?? "Error loading previous import file, please manually add a file or select a data source."});
            }
            else {
                let records = response.data
                if(records.length > 0) {
                    try {
                        let fileUrl = records[0].BlobPath;
                        if(fileUrl.includes("/catalogmanagerfiles/")) {
                            setHasPreviousImport(true);
                            let hashedFileName = fileUrl.split("/").pop();
                            let fileDescription = records[0].Description;

                            let formattedUrl = Config.api + `/api/v1/attachments/catalogmanagerfiles/${hashedFileName}`;
                            let downloadResponse = await axios.get(formattedUrl, {
                                responseType: 'blob',
                                signal: abortController.current.signal
                            });
                            let file = new File([downloadResponse.data], fileDescription);
                            await onUploadDrop([file]);
                        }
                    }
                    catch(e) {
                        if (e.name !== 'AbortError') {
                            setActiveStep(0);
                            setError({open: true, errorHeader: "Error", errorMessage: "Error loading previous import file, please manually add a file or select a data source."});
                        }
                    }
                }
            }
        } catch (e) {
            clearTimeout(timeoutId);
            if (e.name !== 'AbortError') {
                setActiveStep(0);
                setError({open: true, errorHeader: "Error", errorMessage: "Error loading previous import file, please manually add a file or select a data source."});
            }
        }
        setLoadingPreviousImport(false);
    }
    const loadPreviousImports = async () => {
        let previousImports = [];
        setLoadingPreviousFiles(true);

        // Create new abort controller for this request
        const abortController = new AbortController();
        // Set up timeout to abort after 15 seconds
        const timeoutId = setTimeout(() => {
            abortController.abort();
        }, 15000);

        try {
            let response = await utilSearchLastImports(10, abortController.signal);
            clearTimeout(timeoutId);
            if (!response.success) {
                setError({open: true, errorHeader: "Error", errorMessage: "Error pulling previous imports, please manually upload a file."});
            } else {
                response.data.forEach(result => {
                    if(result.BlobPath.includes("/catalogmanagerfiles/")) {
                        previousImports.push({
                            Partner: result.PartnerCoId,
                            Records: [result]
                        });
                    }
                });
            }
        } catch (e) {
            clearTimeout(timeoutId);
            setError({open: true, errorHeader: "Error", errorMessage: e.message || "Error pulling previous imports, please manually upload a file."});
        }

        // if(!props.isRetailer) {
        //     const retailerCount = props.retailers.length;
        //     previousImports = await Promise.all(props.retailers.map(async partner => {
        //         let response = await utilSearchLastPartnerImports(partner.Id, retailerCount > 5 ? 3 : 5);
        //         if (!response.success) {
        //             setError({open: true, errorHeader: "Error", errorMessage: "Error pulling previous imports, please manually upload a file."});
        //             return {Partner: partner.Id, Records: []};
        //         } else {
        //             return {
        //                 Partner: partner.Id,
        //                 Records: response.data.filter(result => result.BlobPath.includes("/catalogmanagerfiles/"))
        //             };
        //         }
        //     }));
        // }

        setLoadingPreviousFiles(false);
        return previousImports;
    }
    //endregion

    //region Lifecycle
    // Effect for initial load and partner changes
    useDidUpdateEffect(() => {
        const handlePartnerChange = async () => {
            setLoading(true);

            // Only load previous import if partner has changed, isMarketplace, and has at least one available profile
            const partnerChanged = previousPartner.current !== profile.Partner;
            const isMarketplace = props.retailers && props.retailers.some(retailer => retailer.Marketplace === true);
            const availableProfilesForPartner = availableProfiles.filter(avalProfile => avalProfile.Partner === profile.Partner)

            if (
                partnerChanged && profile.Partner && profile.Partner !== ""
                && isMarketplace && profile.Partner !== "000000"
                && (availableProfilesForPartner.length > 0)
            ) {
                await loadPreviousImport();
            }

            if((partnerChanged && profile.Partner && profile.Partner !== "" && profile.Partner !== "000000") || props.isRetailer) {
                await loadAttributes(profile.Partner);
            }

            if(!isInitialized.current) {
                setRefreshView(!resfreshView);
            }
            isInitialized.current = true;

            previousPartner.current = profile.Partner;
            setLoading(false);
        };

        handlePartnerChange();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.Partner]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);

            if(urlStartingStep) {
                setActiveStep(parseInt(urlStartingStep));
            }

            const [partnersResult, importProfilesResult, previousImportsResult] = await Promise.all([
                loadPartners(),
                loadImportProfiles(),
            ]);

            let foundProfile = urlPartnerId && importProfilesResult.find(r => r.Partner === parseInt(urlPartnerId));
            foundProfile = (urlPartnerId && urlProfileName) ? importProfilesResult.find(r => r.Name === urlProfileName && r.Partner === parseInt(urlPartnerId)) : foundProfile;
            if(!foundProfile && urlPartnerId) {
                setProfile({...blankProfile, Partner: parseInt(urlPartnerId)});
            }
            else {
                if(importProfilesResult.length > 0 && urlPartnerId) setProfile(foundProfile ? foundProfile : importProfilesResult[0]);
            }

            setPartners(partnersResult ?? []);
            setPreviousImports(previousImportsResult ?? []);

            setLoading(false);
        }

        const loadPreviousFiles = async () => {
            let previousImports = await loadPreviousImports();
            setPreviousImports(previousImports);
        }

        loadPreviousFiles();
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Separate effect for profile formatting and steps
    useEffect(() => {
        const handleProfileFormatting = () => {
            if (parsedFields.length === 0) {
                const existingProfile = availableProfiles.find(r => r.Name === profile.Name);
                setFormattedProfile({
                    Name: profile.Name,
                    Partner: profile.Partner,
                    Description: profile.Description,
                    DocumentType: profile.DocumentType,
                    Id: profile.Id,
                    Shared: profile.Shared,
                    Fields: existingProfile?.Fields || [],
                    Rules: existingProfile?.Rules || [],
                    Filter: ""
                });
                return;
            }

            const formattedColumns = formatProfileToColumns(profile, parsedFields);
            const formattedListMappings = formatProfileListMappingsToRules(profile, parsedFields);
            const formattedCategoryMappings = formatProfileCategoryMappingsToRules(profile, parsedFields);
            const formattedAttributeRules = formatProfileAttributeRulesToRules(profile, parsedFields);

            setFormattedProfile({
                Name: profile.Name,
                Partner: profile.Partner,
                Description: profile.Description,
                DocumentType: profile.DocumentType,
                Fields: formattedColumns,
                Id: profile.Id,
                Shared: profile.Shared,
                Rules: [
                    ...formattedListMappings,
                    ...formattedCategoryMappings,
                    ...formattedAttributeRules
                ],
                Filter: ""
            });
        };

        const updateSteps = () => {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[0].status = file && profile ? "success" : "";
                newState[1].status = profile.Category ? "success" : "";
                newState[2].status = Object.keys(profile.CategoryMappings).length > 0 ? "success" : "";
                newState[3].status = (Object.keys(profile.AttributeMappings).filter(key => key !== "category").length > 0) ? "success" : "";
                newState[4].status = Object.keys(profile.ListMappings).length > 0 ? "success" : "";
                return newState;
            });
        };

        handleProfileFormatting();
        updateSteps();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, parsedFields, file]);

    // Add effect to cleanup abort controller on step change
    useEffect(() => {
        if (abortController.current) {
            abortController.current.abort();
        }
    }, [activeStep]);
    //endregion

    return (
        <>
            <Grid>
                <EnhancedContentLoader updating={updating}/>

                <Grid container style={{gap: "24px", padding: "24px 14px"}}>
                    <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                        <IconButton className={globalClasses.iconButton}
                                    style={{height: "36px", width: "36px"}}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (props.location.key === undefined)
                                            props.history.push("/productonboardingcenter/catalog?catalog=mycatalog");
                                        else
                                            props.history.goBack();
                                    }}>
                            <ArrowBack style={{height: "18px", width: "18px"}}/>
                        </IconButton>

                        <Typography style={{fontSize: "24px", fontWeight: "600"}}>
                            {props.isRetailer
                                ? "Import"
                                : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                                    ? "Source Selection"
                                    : "Import"
                            }
                        </Typography>
                    </Grid>

                    <Paper style={{width: "100%", padding: "6px", borderRadius: "5px"}}>
                        <Grid container>
                            <EnhancedStepper
                                steps={steps}
                                activeStep={activeStep}
                                profile={profile}
                                onStepClick={
                                    (hasPreviousImport && file && profile.Partner && profile.Partner !== "000000") ?
                                        (index) => {
                                            if(hasPreviousImport && file) {
                                                SaveProfile(formattedProfile, profileType);
                                                setActiveStep(index);
                                            }
                                        }
                                        : undefined
                                }
                            />
                        </Grid>
                    </Paper>

                    <Grid container style={{gap: "12px"}}>
                        {getStepContent(activeStep,
                            {
                                retailers: props.retailers,
                                isMarketplace: props.retailers && props.retailers.some(retailer => retailer.Marketplace === true),

                                urlPartnerId: urlPartnerId,
                                previousImports: previousImports,

                                isRetailer: props.isRetailer,

                                allowBack, allowNext, backStep, nextStep, activeStep, setActiveStep, steps, setSteps,

                                profile, setProfile, profileType, availableProfiles, blankProfile, loadImportProfiles,
                                formattedProfile, formatProfileFromRemote,

                                parsedFields, parsedFieldsWithData, sampleData,

                                file, onUploadDrop,

                                partners, retailerAttributes: attributes,

                                error, setError,
                                updating, setUpdating, updated, setUpdated,
                                loading, setLoading,
                                loadingPreviousImport, loadingPreviousFiles,

                                storageProfileName, storageHelper,

                                history: props.history,
                                location: props.location,

                                abortController: abortController,
                                refreshView: resfreshView
                            }
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {/*region Dialogs*/}
            <EnhancedErrorDialog open={error.open} errorHeader={error.errorHeader} errorMessage={error.errorMessage}
                                 onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}/>

            <UpdatingDialog
                title={"Loading"}
                subtitle={"Please wait while we load import context."}
                open={urlStartingStep && (loadingPreviousImport || loading)}
                onClose={() => setLoading(false)}
            />

            <UpdatingDialog
                title={"Loading"}
                subtitle={"Please wait while we load the previous import."}
                open={loadingPreviousImport && false}
                onClose={() => setLoading(false)}
            />
            {/*endregion*/}
        </>
    )
}
